<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import RequirementsEditor from './RequirementsEditor.vue';

// The Requirements (Requisitos) tab. Edits config.requirements: the eligibility + attestation
// checklist evaluated against each applicant. config.requirements_birthdate_attribute names the
// intake date field that enables a computed `age` in eligibility conditions (shared with payment).
const props = defineProps({
  config: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:config']);

const { t } = useI18n();
const store = useStore();

const STANDARD_KEYS = ['age', 'gender', 'colonia', 'city', 'country_code'];

const contactAttributes = useMapGetter('attributes/getContactAttributes');

// Known applicant attribute keys for the picker: account contact custom attributes + standard keys.
const attributeOptions = computed(() => {
  const custom = (contactAttributes.value || []).map(attr => attr.attributeKey);
  return [...new Set([...STANDARD_KEYS, ...custom])].filter(Boolean);
});

const requirements = computed(() =>
  Array.isArray(props.config.requirements) ? props.config.requirements : []
);

function patchConfig(patch) {
  emit('update:config', { ...props.config, ...patch });
}

onMounted(() => {
  store.dispatch('attributes/get');
});
</script>

<template>
  <div class="flex flex-col gap-5 max-w-2xl">
    <div class="flex flex-col gap-0.5">
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('TRAMITE_TYPES.REQUIREMENTS.TITLE') }}
      </h2>
      <p class="text-xs text-n-slate-10">
        {{ t('TRAMITE_TYPES.REQUIREMENTS.HELP') }}
      </p>
    </div>

    <!-- Birthdate attribute (enables a computed `age` in conditions) -->
    <div class="flex flex-col gap-1 max-w-sm">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('TRAMITE_TYPES.REQUIREMENTS.BIRTHDATE_ATTR') }}
      </label>
      <input
        type="text"
        :value="config.requirements_birthdate_attribute ?? ''"
        :placeholder="t('TRAMITE_TYPES.REQUIREMENTS.BIRTHDATE_PLACEHOLDER')"
        class="px-3 py-2 w-full font-mono text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
        @input="patchConfig({ requirements_birthdate_attribute: $event.target.value })"
      />
      <span class="text-xs text-n-slate-10">
        {{ t('TRAMITE_TYPES.REQUIREMENTS.BIRTHDATE_HELP') }}
      </span>
    </div>

    <RequirementsEditor
      :model-value="requirements"
      :attribute-options="attributeOptions"
      @update:model-value="patchConfig({ requirements: $event })"
    />
  </div>
</template>
